// ABOUTME: Component displaying today's positivity as a ring chart with behavior counts and positive/negative ratio
// ABOUTME: Shows stars (positive) vs triangles+squares (negative) in a Material-UI card with Recharts visualization

import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Star as StarIcon } from '@mui/icons-material';
import { TriangleIcon, StormIcon } from '../utils/behaviorUtils';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip
} from 'recharts';
import { TodaysBehaviorCounts } from '../types/api';

interface TodaysPositivityCardProps {
  behaviorCounts: TodaysBehaviorCounts;
  loading?: boolean;
}

interface ChartData {
  name: string;
  value: number;
  color: string;
  icon: React.ElementType;
  actualCount: number;
  [key: string]: string | number | React.ElementType; // Index signature for Recharts compatibility
}

const TodaysPositivityCard: React.FC<TodaysPositivityCardProps> = ({
  behaviorCounts,
  loading = false
}) => {
  const theme = useTheme();
  const { starCount, triangleCount, squareCount } = behaviorCounts;
  
  // Calculate positive ratio (stars vs triangles + squares) as #:1 format
  const totalNegative = triangleCount + squareCount;
  const positiveRatio = totalNegative === 0 
    ? (starCount > 0 ? '∞:1' : '0:1') // Infinite positive or no data
    : `${Math.round(starCount / totalNegative)}:1`;

  // Check if no behaviors recorded
  const totalBehaviors = starCount + triangleCount + squareCount;
  const isEmpty = totalBehaviors === 0;

  // Prepare chart data (only include segments with values > 0)
  const behaviorTypes = [
    { name: 'Stars', count: starCount, color: theme.palette.success.main, icon: StarIcon },
    { name: 'Triangles', count: triangleCount, color: theme.palette.warning.main, icon: TriangleIcon },
    { name: 'Storms', count: squareCount, color: theme.palette.error.main, icon: StormIcon }
  ];
  
  const chartData: ChartData[] = behaviorTypes
    .filter(type => type.count > 0)
    .map(type => ({
      name: type.name,
      value: type.count,
      color: type.color,
      icon: type.icon,
      actualCount: type.count
    }));

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Today&apos;s Positivity
          </Typography>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading...</Typography>
          </Box>
          {/* Explanatory Text */}
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Positivity ratio calculated from stars vs triangles only
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (isEmpty) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Today&apos;s Positivity
          </Typography>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Typography color="text.secondary">
              No behaviors recorded today
            </Typography>
          </Box>
          {/* Explanatory Text */}
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Positivity ratio calculated from stars vs triangles only
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card role="region" aria-label="Today's positivity summary">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Today&apos;s Positivity
        </Typography>
        
        {/* Chart and Legend Layout - Responsive */}
        <Box
          display="flex"
          alignItems="center"
          gap={3}
          flexDirection={{ xs: 'column', md: 'row' }}
          data-testid="chart-legend-container"
        >
          {/* Ring Chart */}
          <Box
            position="relative"
            height={200}
            width={200}
            flexShrink={0}
            data-testid="chart-container"
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                  startAngle={90}
                  endAngle={450}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name) => [`${value}`, name]}
                  labelStyle={{ display: 'none' }}
                />
              </PieChart>
            </ResponsiveContainer>

            {/* Center Ratio Display */}
            <Box
              position="absolute"
              top="50%"
              left="50%"
              sx={{
                transform: 'translate(-50%, -50%)',
                textAlign: 'center'
              }}
            >
              <Typography variant="h4" fontWeight="bold" color="primary">
                {positiveRatio}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Positive
              </Typography>
            </Box>
          </Box>

          {/* Legend - Responsive layout */}
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            data-testid="legend-container"
          >
            {behaviorTypes
              .filter(type => type.count > 0)
              .map(type => {
                const IconComponent = type.icon;
                // Handle singular/plural properly
                const getSingularName = (name: string) => {
                  if (name === 'Stars') return 'Star';
                  if (name === 'Triangles') return 'Triangle';
                  if (name === 'Storms') return 'Storm';
                  return name;
                };

                const displayName = type.count === 1
                  ? getSingularName(type.name)
                  : type.name;

                return (
                  <Box key={type.name} display="flex" alignItems="center" gap={1}>
                    <IconComponent sx={{ color: type.color, fontSize: 20 }} />
                    <Typography variant="body1">
                      {type.count} {displayName}
                    </Typography>
                  </Box>
                );
              })}
          </Box>
        </Box>

        {/* Explanatory Text */}
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Positivity ratio calculated from stars vs triangles only
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default TodaysPositivityCard;